import { useRef, useState } from 'react';
import { IcoDownload } from './EditIcons';
import { uploadFile, addTalentMedia, deleteTalentMedia } from '../../../../api';
import { TALENT_PLACEHOLDER_SQUARE, markTalentProfileImageRemoved } from '../../../../shared/utils/profilePlaceholders';
import LoadingSpinner from '../../../../shared/components/LoadingSpinner';
import styles from './EditPhotoSection.module.css';

const TALENT_PLACEHOLDER = TALENT_PLACEHOLDER_SQUARE;

const MAX_PHOTOS = 4;
const PHOTO_ORDER_KEY = 'talent-photo-order';

const ITEM_W = 220;
const ITEM_H = 280;

function PhotoItem({ src, onRemove, onDragStart, onDragOver, onDrop, isDragging, isDropTarget }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        width: ITEM_W,
        height: ITEM_H,
        borderRadius: '10px',
        overflow: 'hidden',
        flexShrink: 0,
        opacity: isDragging ? 0.35 : 1,
        outline: isDropTarget ? '2px solid #000' : 'none',
        transition: 'opacity 0.15s, outline 0.1s',
        cursor: 'grab',
      }}
    >
      <img
        src={src}
        alt="추가 사진"
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
        onError={e => { e.currentTarget.style.display = 'none'; }}
      />

      {/* Hover overlay + centered controls */}
      {hovered && (
        <div style={{
          position: 'absolute', inset: 0,
          background: 'rgba(0,0,0,0.30)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: '10px',
          zIndex: 3,
        }}>
          {/* Drag handle */}
          <div style={{
            width: 44, height: 44, borderRadius: '10px',
            background: 'rgba(255,255,255,0.90)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'grab', fontSize: '20px', color: '#333', lineHeight: 1,
            pointerEvents: 'none',
          }}>
            ⠿
          </div>

          {/* X button */}
          <button
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            style={{
              width: 44, height: 44, borderRadius: '10px',
              background: 'rgba(255,255,255,0.90)', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              pointerEvents: 'auto',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="#333" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

export default function EditPhotoSection({ talentProfileId, photos, setPhotos, initialMainPhoto, initialMainPhotoId = null, onMainPhotoUploaded, pendingMainFile, setPendingMainFile, pendingDeleteMainPhotoId, setPendingDeleteMainPhotoId }) {
  const fileRef = useRef(null);
  const addPhotoRef = useRef(null);
  const [mainPreview, setMainPreview] = useState(initialMainPhoto || null);
  const [mainPhotoId, setMainPhotoId] = useState(initialMainPhotoId);
  const [isUploadingAdd, setIsUploadingAdd] = useState(false);
  const dragIdx = useRef(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  function persistPhotoOrder(nextPhotos) {
    if (!talentProfileId) return;
    try {
      const raw = localStorage.getItem(PHOTO_ORDER_KEY);
      const all = raw ? JSON.parse(raw) : {};
      all[String(talentProfileId)] = nextPhotos.map((photo) => String(photo.id ?? photo.url));
      localStorage.setItem(PHOTO_ORDER_KEY, JSON.stringify(all));
    } catch {}
  }

  function handleMainPhoto(e) {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const localPreview = URL.createObjectURL(file);
    setMainPreview(localPreview);
    // 즉시 API 호출 안 함 — 저장 버튼 눌릴 때 부모에서 처리
    setPendingMainFile?.(file);
    setPendingDeleteMainPhotoId?.(null);
  }

  function handleRemoveMainPhoto(e) {
    e.stopPropagation();
    // 삭제도 pending으로 — 저장 버튼 눌릴 때 부모에서 처리
    setPendingMainFile?.(null);
    setPendingDeleteMainPhotoId?.(mainPhotoId);
    setMainPreview(null);
    setMainPhotoId(null);
    if (fileRef.current) fileRef.current.value = '';
  }

  async function handleAddPhoto(e) {
    const file = e.target.files[0];
    const inputEl = e.target;
    inputEl.value = '';
    if (!file) return;
    if (photos.length >= MAX_PHOTOS) return;
    const localPreview = URL.createObjectURL(file);
    setPhotos(prev => {
      const next = [...prev, { id: null, url: localPreview }];
      persistPhotoOrder(next);
      return next;
    });
    if (!talentProfileId) return;
    setIsUploadingAdd(true);
    try {
      const { fileEntityId, url } = await uploadFile(file, 'IMAGE');
      const media = await addTalentMedia({ talentProfileId: Number(talentProfileId), fileEntityId, mediaType: 'IMAGE', photoType: 'PORTFOLIO' });
      setPhotos(prev => {
        const next = prev.map(p => p.url === localPreview ? { id: media?.id ?? null, url: url || localPreview } : p);
        persistPhotoOrder(next);
        return next;
      });
    } catch {
      alert('추가 사진 업로드에 실패했습니다.');
      setPhotos(prev => {
        const next = prev.filter(p => p.url !== localPreview);
        persistPhotoOrder(next);
        return next;
      });
    } finally {
      setIsUploadingAdd(false);
    }
  }

  async function removePhoto(idx) {
    const photo = photos[idx];
    // 서버에 저장된 사진이면 삭제 API 호출
    if (photo?.id) {
      try {
        await deleteTalentMedia(photo.id);
      } catch {
        alert('사진 삭제에 실패했습니다.');
        return;
      }
    }
    setPhotos(prev => {
      const next = prev.filter((_, i) => i !== idx);
      persistPhotoOrder(next);
      return next;
    });
  }

  function handleDragStart(idx) {
    dragIdx.current = idx;
  }

  function handleDragOver(e, idx) {
    e.preventDefault();
    setDragOverIdx(idx);
  }

  function handleDrop(idx) {
    const from = dragIdx.current;
    setDragOverIdx(null);
    if (from === null || from === idx) return;
    setPhotos(prev => {
      const arr = [...prev];
      const [moved] = arr.splice(from, 1);
      arr.splice(idx, 0, moved);
      persistPhotoOrder(arr);
      return arr;
    });
    dragIdx.current = null;
  }

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>사진 포트폴리오</h2>
      <div className={styles.photoGrid}>

        {/* 대표 사진 */}
        <div className={`${styles.card} ${styles.mainCard}`}>
          <div className={styles.cardHeaderFixed}>
            <p className={styles.cardLabel}>프로필 대표 사진</p>
            <p className={styles.cardDesc}>
              프로필에 표시될 대표 사진입니다.<br />얼굴이 잘 보이는 정면 사진을 권장합니다.
            </p>
          </div>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleMainPhoto} />
          <div
            onClick={() => fileRef.current?.click()}
            className={styles.uploadBox}
            style={{ cursor: 'pointer', position: 'relative' }}
          >
            {/* 사진 있으면 실제 사진, 없으면 디폴트 아이콘 */}
            <img
              src={mainPreview || TALENT_PLACEHOLDER}
              alt="대표 사진"
              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }}
            />
            {/* 업로드 중 오버레이 */}
            {/* 실제 사진이 있을 때만 X 버튼 */}
            {mainPreview && (
              <button
                onClick={handleRemoveMainPhoto}
                style={{ position: 'absolute', top: 8, right: 8, width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"/></svg>
              </button>
            )}
          </div>
          <p className={styles.footerHint}>권장 크기: 400x400px 이상 / 최대 5MB / JPG, PNG 형식</p>
        </div>

        {/* 추가 사진 */}
        <div className={`${styles.card} ${styles.subCard}`}>
          <div className={styles.cardHeaderFixed}>
            <p className={styles.cardLabel}>추가 사진</p>
            <p className={styles.cardDesc}>
              다양한 각도와 스타일의 사진을 추가하여 프로필을 더 풍성하게 만들어보세요.
            </p>
          </div>
          <div className={styles.scrollArea}>
            {photos.map((photo, i) => (
              <PhotoItem
                key={(photo.id ?? photo.url) + i}
                src={photo.url}
                onRemove={() => removePhoto(i)}
                onDragStart={() => handleDragStart(i)}
                onDragOver={(e) => handleDragOver(e, i)}
                onDrop={() => handleDrop(i)}
                isDragging={dragIdx.current === i}
                isDropTarget={dragOverIdx === i && dragIdx.current !== i}
              />
            ))}
            {photos.length < MAX_PHOTOS && (
              <>
                <input ref={addPhotoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAddPhoto} />
                <div
                  onClick={() => !isUploadingAdd && addPhotoRef.current?.click()}
                  className={styles.subUploadBox}
                  style={{ cursor: isUploadingAdd ? 'default' : 'pointer', opacity: isUploadingAdd ? 0.6 : 1 }}
                >
                  {isUploadingAdd ? <LoadingSpinner size="sm" style={{ width: 28, height: 28 }} /> : <IcoDownload />}
                  <span className={styles.hintTextCenter}>{isUploadingAdd ? '업로드 중...' : '사진 추가'}</span>
                </div>
              </>
            )}
          </div>
          <p className={styles.cardDesc}>
            클릭하여 순서를 변경할 수 있습니다. 최대 {MAX_PHOTOS}장까지 등록 가능합니다.
          </p>
        </div>
      </div>
    </div>
  );
}
